import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * 3.4 (DCMPDEV-699): a pending parent (whoami pendingEnrollment=true) may only
 * see their own pages — home, the child page, the wizard and My profile.
 * Every other dashboard route redirects to home (4.15: the portal is organised
 * by child). Active parents and staff pass through unchanged.
 *
 * Runs AFTER authGuard (the shell is already authenticated). The check is a
 * fresh whoami so a parent who has just been activated is no longer confined.
 *
 * 2.16 P2 (DCMPDEV-795): FAIL CLOSED. If whoami errors we cannot prove the
 * parent is unconfined, so we redirect to the enrollment page (an active parent
 * landing there is harmless; a pending parent escaping confinement is not).
 * The only pass-through is the enrollment pages themselves (route data) and an
 * unauthenticated request, which authGuard owns.
 */
export const pendingParentGuard: CanActivateFn = (route): ReturnType<CanActivateFn> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Route data may opt a path out of the confinement (the enrollment pages).
  if (route.data?.['allowPendingParent'] === true) return true;

  // No auth yet: authGuard owns that decision; don't double-redirect.
  if (!auth.isAuthenticated) return true;

  return auth
    .whoamiRaw()
    .pipe(
      map((who): boolean | UrlTree =>
        who.pendingEnrollment ? router.parseUrl('/dashboard') : true,
      ),
      // Fail closed: a whoami failure cannot prove the parent is unconfined.
      catchError(() => of(router.parseUrl('/dashboard'))),
    );
};
